import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import Input from '../../components/form/input/InputField';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminCard from '../../components/admin/AdminCard';
import AdminListToolbar from '../../components/admin/AdminListToolbar';
import {
  adminAlertErrorClass,
  adminEditLinkClass,
  adminTableClass,
  adminTableEmptyClass,
  adminTableHeadClass,
  adminTableTdClass,
  adminTableThClass,
  adminTableWrapperClass,
} from '../../components/admin/adminStyles';
import { SEO_SITE_OPTIONS, siteLabel, type SeoRouteRecord } from './seoRouteTypes';

export default function SeoRoutes() {
  const [items, setItems] = useState<SeoRouteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [siteKey, setSiteKey] = useState('southampton-port-taxi');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const collected: SeoRouteRecord[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 20; page += 1) {
        const params = new URLSearchParams({ limit: '500' });
        if (siteKey) params.set('siteKey', siteKey);
        if (cursor) params.set('cursor', cursor);
        const response = await axiosInstance.get(`/seo-routes?${params}`);
        const data = response.data as {
          items: SeoRouteRecord[];
          nextCursor: string | null;
        };
        collected.push(...(data.items ?? []));
        cursor = data.nextCursor;
        if (!cursor) break;
      }
      const q = searchTerm.trim().toLowerCase();
      const filtered = q
        ? collected.filter(
            (row) =>
              row.routeSlug.toLowerCase().includes(q) ||
              row.path.toLowerCase().includes(q) ||
              row.from.name.toLowerCase().includes(q) ||
              row.to.name.toLowerCase().includes(q) ||
              row.seo.metaTitle.toLowerCase().includes(q),
          )
        : collected;
      setItems(filtered);
    } catch {
      setError('Failed to fetch SEO routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [siteKey, searchTerm]);

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle="SEO Routes"
        description="Edit per-site taxi corridor titles, descriptions, and intro copy without redeploying brand sites."
      />

      <AdminCard>
        <AdminListToolbar>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <select
                value={siteKey}
                onChange={(e) => setSiteKey(e.target.value)}
                className="h-11 rounded-lg border border-gray-200 bg-white px-4 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              >
                {SEO_SITE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Input
                type="text"
                placeholder="Search slug, places, or title…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </AdminListToolbar>

        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}

        <div className={adminTableWrapperClass}>
          <table className={adminTableClass}>
            <thead className={adminTableHeadClass}>
              <tr>
                <th className={adminTableThClass}>Site</th>
                <th className={adminTableThClass}>Route</th>
                <th className={adminTableThClass}>From → To</th>
                <th className={adminTableThClass}>Meta title</th>
                <th className={adminTableThClass}>Published</th>
                <th className={adminTableThClass}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className={adminTableEmptyClass}>
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className={adminTableEmptyClass}>
                    No routes found
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id}>
                    <td className={adminTableTdClass}>{siteLabel(row.siteKey)}</td>
                    <td className={adminTableTdClass}>
                      <div className="font-medium">{row.routeSlug}</div>
                      <div className="text-xs text-gray-500">{row.path}</div>
                    </td>
                    <td className={adminTableTdClass}>
                      {row.from.name} → {row.to.name}
                    </td>
                    <td className={adminTableTdClass}>{row.seo.metaTitle}</td>
                    <td className={adminTableTdClass}>
                      {row.published ? 'Yes' : 'No'}
                    </td>
                    <td className={adminTableTdClass}>
                      <Link to={`/seo-routes/edit/${row.id}`} className={adminEditLinkClass}>
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </AdminPageShell>
  );
}
